import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { NO_PROJECT_ID, type Project } from '@deposits/shared';
import { useProjects } from '../hooks/useProjects';
import { useBoxSettings } from '../hooks/useBoxSettings';

const SELECTED_PROJECT_KEY = 'deposits.selected.project_id';

interface ProjectContextValue {
  projects: Project[];
  /** The project every scoped request sends as `project_id`. null when the box
   *  doesn't manage projects — requests then omit it and the server applies no
   *  project filter (today's behaviour). */
  selectedProjectId: number | null;
  setSelectedProjectId: (id: number) => void;
  selectedProject: Project | null;
  /** Whether the box separates data per Arbox project (opt-in setting). */
  manageProjects: boolean;
}

const ProjectContext = createContext<ProjectContextValue | null>(null);

/** Second scope layer under LocationProvider — same shape and localStorage
 *  persistence as LocationContext, but off unless the box opts in. */
export function ProjectProvider({ children }: { children: ReactNode }) {
  const { data: settings } = useBoxSettings();
  const manageProjects = settings?.manage_projects ?? false;
  const { data: projects = [] } = useProjects(manageProjects);
  const [storedId, setStoredId] = useState<number>(() => {
    try {
      const cached = localStorage.getItem(SELECTED_PROJECT_KEY);
      return cached != null ? Number(cached) : NO_PROJECT_ID;
    } catch {
      return NO_PROJECT_ID;
    }
  });

  const setSelectedProjectId = (id: number) => {
    setStoredId(id);
    try {
      localStorage.setItem(SELECTED_PROJECT_KEY, String(id));
    } catch {
      /* storage unavailable — selection just won't persist */
    }
  };

  // Fall back to "no project" if the stored id isn't one of the box's projects
  // (another box's selection, or a project that's gone).
  useEffect(() => {
    if (projects.length === 0) return;
    if (!projects.some((p) => p.id === storedId)) setSelectedProjectId(NO_PROJECT_ID);
  }, [projects, storedId]);

  const selectedProjectId = manageProjects ? storedId : null;
  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId) ?? null,
    [projects, selectedProjectId],
  );

  const value: ProjectContextValue = {
    projects,
    selectedProjectId,
    setSelectedProjectId,
    selectedProject,
    manageProjects,
  };
  return <ProjectContext.Provider value={value}>{children}</ProjectContext.Provider>;
}

export function useProjectContext(): ProjectContextValue {
  const ctx = useContext(ProjectContext);
  if (!ctx) throw new Error('useProjectContext must be used inside ProjectProvider');
  return ctx;
}
